import { CalendarDays, CircleDollarSign } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getChannelDefaults } from '@/components/pages/job-posting/channel-defaults';
import { ZIPRECRUITER_BUDGET_PRESETS } from '@/components/pages/job-posting/validation/constants';
import { Input } from '@/components/ui/input';
import { FormSelect } from '@/components/ui/select';
import { Typography } from '@/components/ui/typography';
import { ZIPRECRUITER } from '@/types/channels';
import { addDays, formatShortDate } from '@/utils/date-util';

import type { GeneralRecommendationForm } from '../types';

interface ChannelEditProps {
  channelSlug: string;
  hookForm: GeneralRecommendationForm;
}

/** Empty input → `undefined`; non-numeric input → `undefined`; otherwise the number. */
function emptyOrNumber(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function ChannelEdit({ channelSlug, hookForm }: ChannelEditProps) {
  const intl = useIntl();
  const {
    register,
    formState: { errors },
    watch,
    control,
  } = hookForm;

  const duration = watch(`${channelSlug}.duration`);

  const channelDefaults = getChannelDefaults(channelSlug);
  const isBudgetReadOnly = !!channelDefaults.budget;
  const isDurationReadOnly = !!channelDefaults.duration;

  return (
    <>
      <div className="flex gap-4">
        {/* Budget input */}
        {channelSlug === ZIPRECRUITER ? (
          <FormSelect
            name={`${channelSlug}.budget`}
            control={control}
            options={ZIPRECRUITER_BUDGET_PRESETS}
            isMultiSelect={false}
            className="rounded-sm"
            label={intl.formatMessage({
              defaultMessage: 'Budget',
              description: 'Form field label for spend on a recommended board',
            })}
            placeholder={intl.formatMessage({
              defaultMessage: 'Select budget',
              description:
                'Dropdown prompt for ZipRecruiter preset spend tiers',
            })}
          />
        ) : (
          <Input
            isNumber
            {...register(`${channelSlug}.budget`, {
              setValueAs: emptyOrNumber,
            })}
            label={intl.formatMessage({
              defaultMessage: 'Budget',
              description: 'Form field label for spend on a recommended board',
            })}
            placeholderIcon={
              <CircleDollarSign className="text-fx-neutral-400 size-4" />
            }
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter budget',
              description:
                'Numeric input hint for lifetime spend on this channel row',
            })}
            error={errors?.[channelSlug]?.budget?.message}
            readOnly={isBudgetReadOnly}
          />
        )}

        {/* Duration input */}
        <Input
          isNumber
          {...register(`${channelSlug}.duration`, {
            setValueAs: emptyOrNumber,
          })}
          label={intl.formatMessage({
            defaultMessage: 'Duration (Days)',
            description:
              'Form field label for how long the campaign should run in days',
          })}
          placeholderIcon={
            <CalendarDays className="text-fx-neutral-400 size-4" />
          }
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter duration',
            description: 'Numeric input hint for campaign length in days',
          })}
          error={errors?.[channelSlug]?.duration?.message}
          readOnly={isDurationReadOnly}
        />
      </div>

      <Typography variant="small1" className="!text-fx-neutral-600">
        <FormattedMessage
          defaultMessage="New campaign end date will be: {endDate}"
          description="Clarifies computed end date from today plus selected duration"
          values={{
            endDate:
              duration != null && Number.isFinite(duration)
                ? formatShortDate(addDays(new Date(), duration))
                : '-',
          }}
        />
      </Typography>
    </>
  );
}
